// Background music / focus sounds. Off by default; when enabled a compact player
// mounts at the bottom-left (a body child, so it rides above proxied tabs and
// survives every render). Two sources: built-in soundscapes generated live with the
// Web Audio API (no files, no external hosts - safe under the app's cross-origin
// isolation), and your own uploaded tracks (stored only in this browser via
// music-store.js). Playback starts on a user gesture, per browser autoplay rules.
import { get, set, subscribe } from "../shared/settings-store.js";
import { MUSIC_PRESET_MAP } from "../shared/settings-schema.js";
import { listTracks, subscribeTracks, getTrackBlob } from "../shared/music-store.js";
import { emitIsland } from "./island-gate.js";

const PLAYING_KEY = "revault:music-playing"; // sessionStorage: survive a safe auto-reload

// --- Web Audio soundscape engine -------------------------------------------
function fill(data, kind) {
	if (kind === "white") { for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1; return; }
	if (kind === "brown") { let last = 0; for (let i = 0; i < data.length; i++) { const w = Math.random() * 2 - 1; last = (last + 0.02 * w) / 1.02; data[i] = last * 3.4; } return; }
	// pink (Paul Kellet)
	let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
	for (let i = 0; i < data.length; i++) {
		const w = Math.random() * 2 - 1;
		b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
		b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
		data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11; b6 = w * 0.115926;
	}
}
function noiseBuffer(ctx, seconds, kind) {
	const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
	fill(buf.getChannelData(0), kind);
	return buf;
}
function looper(ctx, kind, seconds) {
	const src = ctx.createBufferSource();
	src.buffer = noiseBuffer(ctx, seconds || 3, kind);
	src.loop = true;
	return src;
}
// A slow LFO added onto an AudioParam around its current value (for waves / flicker).
function lfo(ctx, freq, depth, param) {
	const o = ctx.createOscillator(); o.frequency.value = freq;
	const g = ctx.createGain(); g.gain.value = depth;
	o.connect(g); g.connect(param); o.start();
	return o;
}

// Build a soundscape graph onto `dest`; returns { stop } that tears it all down.
function buildSoundscape(ctx, id, dest) {
	const nodes = [];
	const timers = [];
	const add = (n) => { nodes.push(n); return n; };
	const g = (v) => { const n = ctx.createGain(); n.gain.value = v; return add(n); };
	const filt = (type, freq, q) => { const n = ctx.createBiquadFilter(); n.type = type; n.frequency.value = freq; if (q != null) n.Q.value = q; return add(n); };

	if (id === "white") {
		const s = add(looper(ctx, "white")); const out = g(0.5);
		s.connect(out); out.connect(dest); s.start();
	} else if (id === "brown") {
		const s = add(looper(ctx, "brown")); const out = g(0.95);
		s.connect(out); out.connect(dest); s.start();
	} else if (id === "rain") {
		const s = add(looper(ctx, "white")); const bp = filt("bandpass", 2600, 0.5); const hp = filt("highpass", 700); const out = g(0.5);
		s.connect(bp); bp.connect(hp); hp.connect(out); out.connect(dest); s.start();
		add(lfo(ctx, 0.15, 0.12, out.gain)); // gentle intensity drift
		const s2 = add(looper(ctx, "brown")); const lp2 = filt("lowpass", 900); const g2 = g(0.25);
		s2.connect(lp2); lp2.connect(g2); g2.connect(dest); s2.start(); // background hush
	} else if (id === "ocean") {
		const s = add(looper(ctx, "pink", 5)); const lp = filt("lowpass", 650); const out = g(0.55);
		s.connect(lp); lp.connect(out); out.connect(dest); s.start();
		add(lfo(ctx, 0.09, 0.4, out.gain)); // the swell of waves
	} else if (id === "fire") {
		const s = add(looper(ctx, "brown")); const lp = filt("lowpass", 480); const out = g(0.7);
		s.connect(lp); lp.connect(out); out.connect(dest); s.start();
		add(lfo(ctx, 0.6, 0.18, out.gain));
		// crackle: brief high-pass noise pops at random intervals
		const crackle = () => {
			try {
				const p = ctx.createBufferSource(); p.buffer = noiseBuffer(ctx, 0.06, "white");
				const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 3500;
				const pg = ctx.createGain(); const t = ctx.currentTime;
				pg.gain.setValueAtTime(0.0001, t); pg.gain.exponentialRampToValueAtTime(0.25 + Math.random() * 0.2, t + 0.005); pg.gain.exponentialRampToValueAtTime(0.0001, t + 0.05 + Math.random() * 0.06);
				p.connect(hp); hp.connect(pg); pg.connect(dest); p.start(); p.stop(t + 0.15);
			} catch (e) {}
			timers.push(setTimeout(crackle, 120 + Math.random() * 500));
		};
		crackle();
	} else { // focus - a soft low drone (perfect fifth) with slow movement
		const lp = filt("lowpass", 420); const out = g(0.5);
		[110, 164.8].forEach((f, i) => { const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f; o.detune.value = i ? 4 : -4; add(o); o.connect(lp); o.start(); });
		lp.connect(out); out.connect(dest);
		add(lfo(ctx, 0.05, 120, lp.frequency)); // filter sweeps slowly
	}

	return {
		stop() {
			timers.forEach(clearTimeout);
			nodes.forEach((n) => { try { if (n.stop) n.stop(); } catch (e) {} try { n.disconnect(); } catch (e) {} });
		},
	};
}

// --- player controller -----------------------------------------------------
let ctx = null, master = null;
let scape = null; // active soundscape { stop }
let audio = null; // HTMLAudioElement for uploads
let objUrl = null; // current object URL (revoked on change)
let order = []; // upload track ids in play order
let pos = 0;
let playing = false;

function vol() { return Math.max(0, Math.min(100, Number(get("musicVolume"))) || 0) / 100; }

function ensureCtx() {
	if (!ctx) {
		ctx = new (window.AudioContext || window.webkitAudioContext)();
		master = ctx.createGain();
		master.connect(ctx.destination);
	}
	if (ctx.state === "suspended") { try { ctx.resume(); } catch (e) {} }
	master.gain.value = vol();
	return ctx;
}
function ensureAudio() {
	if (audio) return audio;
	audio = new Audio();
	audio.preload = "auto";
	audio.addEventListener("ended", () => { if (get("musicPreset") === "uploads") next(); });
	audio.addEventListener("error", () => { if (playing && get("musicPreset") === "uploads" && order.length > 1) next(); });
	return audio;
}

function stopScape() { if (scape) { try { scape.stop(); } catch (e) {} scape = null; } }
function stopAudio() { if (audio) { try { audio.pause(); } catch (e) {} } }

async function loadUpload(i) {
	const a = ensureAudio();
	order = listTracks().map((t) => t.id);
	if (!order.length) { updatePlayer(); return false; }
	pos = ((i % order.length) + order.length) % order.length;
	const blob = await getTrackBlob(order[pos]);
	if (!blob) { return false; }
	if (objUrl) { try { URL.revokeObjectURL(objUrl); } catch (e) {} }
	objUrl = URL.createObjectURL(blob);
	a.src = objUrl;
	a.loop = order.length === 1 && get("musicLoop") !== false;
	a.volume = vol();
	return true;
}

async function startSource() {
	const preset = get("musicPreset") || "brown";
	if (preset === "uploads") {
		stopScape();
		const ok = await loadUpload(pos);
		if (!ok) { playing = false; setFlag(false); updatePlayer(); return; }
		try { await ensureAudio().play(); } catch (e) { playing = false; }
	} else {
		stopAudio();
		ensureCtx();
		stopScape();
		scape = buildSoundscape(ctx, preset, master);
	}
	updatePlayer();
}

function stopAll() { stopScape(); stopAudio(); }

function play() {
	focus = null; // the student's own music takes over from a focus soundscape
	playing = true; setFlag(true);
	startSource();
}

// --- focus sounds (docs/HOME-2026-10.md, F4 stage B) -------------------------
// A soundscape that plays while a Pomodoro focus runs, asked for by the timer tool. Works even
// with the music player off, never touches the player's own state, and steps aside when the
// student's own music is already playing. Breaks dim it to 30%.
let focus = null, focusLow = false;
function focusGain() { if (master) master.gain.value = vol() * (focusLow ? 0.3 : 1); }
export function focusPlay(preset) {
	if (playing || !MUSIC_PRESET_MAP[preset]) return;
	focusLow = false;
	if (focus !== preset || !scape) {
		ensureCtx();
		stopScape();
		scape = buildSoundscape(ctx, preset, master);
		focus = preset;
	} else ensureCtx();
	focusGain();
}
export function focusDim() { if (focus && !playing) { focusLow = true; focusGain(); } }
export function focusStop() { if (focus && !playing) stopScape(); focus = null; focusLow = false; }
export function focusOn() { return !!focus; }
function pause() {
	playing = false; setFlag(false);
	stopAll();
	updatePlayer();
}
function toggle() { playing ? pause() : play(); }
// The island's Media section and its now-playing activity drive the same player.
export const musicCtl = { toggle, next, prev, playing: () => playing };

function next() { if (get("musicPreset") !== "uploads") return; loadUpload(pos + 1).then((ok) => { if (ok && playing) ensureAudio().play().catch(() => {}); updatePlayer(); }); }
function prev() { if (get("musicPreset") !== "uploads") return; loadUpload(pos - 1).then((ok) => { if (ok && playing) ensureAudio().play().catch(() => {}); updatePlayer(); }); }

function setFlag(on) { try { sessionStorage.setItem(PLAYING_KEY, on ? "1" : "0"); } catch (e) {} }
function wasPlaying() { try { return sessionStorage.getItem(PLAYING_KEY) === "1"; } catch (e) { return false; } }

// --- mini-player UI --------------------------------------------------------
let el = null, elPlay = null, elLabel = null, elVol = null, elPrev = null, elNext = null;
const ICON = {
	play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
	pause: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h3v14H7zM14 5h3v14h-3z"/></svg>',
	prev: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 6h2v12H7zM19 6l-9 6 9 6z"/></svg>',
	next: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15 6h2v12h-2zM5 6l9 6-9 6z"/></svg>',
	music: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
};
function iconBtn(cls, label, svg, onClick) {
	const b = document.createElement("button");
	b.type = "button"; b.className = cls; b.setAttribute("aria-label", label); b.title = label; b.innerHTML = svg;
	b.addEventListener("click", onClick);
	return b;
}
function buildPlayer() {
	if (el) return el;
	el = document.createElement("div");
	el.className = "rv-music";
	el.hidden = true;
	const ic = document.createElement("span"); ic.className = "rv-music-ic"; ic.innerHTML = ICON.music;
	elPrev = iconBtn("rv-music-btn", "Previous track", ICON.prev, prev);
	elPlay = iconBtn("rv-music-btn rv-music-play", "Play", ICON.play, toggle);
	elNext = iconBtn("rv-music-btn", "Next track", ICON.next, next);
	elLabel = document.createElement("span"); elLabel.className = "rv-music-label";
	elVol = document.createElement("input"); elVol.type = "range"; elVol.min = "0"; elVol.max = "100"; elVol.className = "rv-music-vol"; elVol.setAttribute("aria-label", "Volume");
	elVol.value = String(Number(get("musicVolume")) || 0);
	elVol.addEventListener("input", () => set("musicVolume", Number(elVol.value)));
	el.append(ic, elPrev, elPlay, elNext, elLabel, elVol);
	document.body.appendChild(el);
	return el;
}
function updatePlayer() {
	if (!el) return;
	const on = get("musicEnabled") === true;
	el.hidden = !on;
	if (!on) { emitIsland("music", { playing: false }); return; }
	const uploads = get("musicPreset") === "uploads";
	elPrev.hidden = !uploads; elNext.hidden = !uploads;
	elPlay.innerHTML = playing ? ICON.pause : ICON.play;
	elPlay.title = playing ? "Pause" : "Play"; elPlay.setAttribute("aria-label", playing ? "Pause" : "Play");
	let label;
	if (uploads) {
		const tracks = listTracks();
		label = tracks.length ? (tracks[pos] ? tracks[pos].name : tracks[0].name) : "No tracks - add in Settings";
	} else {
		const p = MUSIC_PRESET_MAP[get("musicPreset") || "brown"];
		label = p ? p.label : "Music";
	}
	elLabel.textContent = label;
	emitIsland("music", { playing, label, uploads });
	const v = String(Number(get("musicVolume")) || 0);
	if (document.activeElement !== elVol && elVol.value !== v) elVol.value = v;
}

// --- lifecycle -------------------------------------------------------------
function applyVolumeLive() {
	if (master) master.gain.value = vol() * (focus && focusLow ? 0.3 : 1);
	if (audio) audio.volume = vol();
}

export function initMusic() {
	buildPlayer();
	updatePlayer();

	let lastEnabled = get("musicEnabled");
	let lastPreset = get("musicPreset");
	let lastLoop = get("musicLoop");
	subscribe(() => {
		const enabled = get("musicEnabled");
		if (enabled !== lastEnabled) {
			lastEnabled = enabled;
			if (!enabled && playing) { pause(); } // hides via updatePlayer(); a focus soundscape keeps going
		}
		const preset = get("musicPreset");
		if (preset !== lastPreset) { lastPreset = preset; pos = 0; if (playing) startSource(); }
		const loop = get("musicLoop");
		if (loop !== lastLoop) { lastLoop = loop; if (audio) audio.loop = order.length === 1 && loop !== false; }
		applyVolumeLive();
		updatePlayer();
	});
	// A track added/removed elsewhere: refresh the label + play order.
	subscribeTracks(() => { if (get("musicPreset") === "uploads") { order = listTracks().map((t) => t.id); if (pos >= order.length) pos = 0; } updatePlayer(); });

	// Resume across a safe auto-reload: playback needs a gesture, so re-arm on the
	// first interaction (same pattern as ads) if music was playing before the reload.
	if (get("musicEnabled") === true && wasPlaying()) {
		const resume = () => { if (get("musicEnabled") === true && !playing) play(); };
		["pointerdown", "keydown", "touchstart"].forEach((ev) => window.addEventListener(ev, resume, { once: true, passive: true }));
	} else {
		setFlag(false);
	}
}
